<script setup lang="ts">
import { BadgeComponent, type CampaignListItem, CampaignStatusEnum, type CampaignTypeEnum } from '@metapic-apps/shared'
import { computed } from 'vue'

import { numberMinimiseFormat } from '@/legacy/store/helpFunctions'
import { formatDateTimeLong, formatDateTimeRelative } from '@/lib/formatters'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import ImageComponent from '@/ui-library/atoms/ImageComponent.vue'
import TooltipComponent from '@/ui-library/atoms/TooltipComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const emit = defineEmits(['open'])
const props = defineProps<{
  campaign: CampaignListItem
  hasUpdates?: boolean
}>()

const clickValues = computed(() => {
  const { maxClicks = 0, currentClicks = 0 } = props.campaign
  return {
    actual: numberMinimiseFormat(currentClicks),
    max: numberMinimiseFormat(maxClicks),
    progress: Math.min(Math.round(currentClicks / (maxClicks / 100)), 100),
  }
})

const campaignTypeLabels: Record<CampaignTypeEnum, string> = {
  user_accept: 'Influencer approval',
  standard: 'Standard campaign',
  store_accept: 'Brand approval',
  suggestion: 'Suggestion',
}

const campaignStatusMap = {
  [CampaignStatusEnum.Active]: 'Active',
  [CampaignStatusEnum.Paused]: 'Paused',
  [CampaignStatusEnum.Scheduled]: 'Scheduled',
  [CampaignStatusEnum.Ended]: 'Ended',
}

const validUntil = computed(() => {
  if (props.campaign.validUntil && props.campaign.validUntil !== '0000-00-00 00:00:00') {
    return props.campaign.validUntil
  }

  return undefined
})

const endDate = computed(() => (validUntil.value ? formatDateTimeRelative(validUntil.value) : undefined))
const endDateFull = computed(() => (validUntil.value ? formatDateTimeLong(validUntil.value) : undefined))

const handleCardClick = () => {
  emit('open')
}
</script>

<template>
  <FlexComponent
    :class="['card', 'card--clickable', { 'campaign-card__updates-indicator': hasUpdates }]"
    :gap="16"
    justify="space-between"
    align="center"
    @click="handleCardClick"
  >
    <div class="card__content">
      <BoxComponent class="card__title" :mb="8">
        <TypographyComponent class="card__title__i" variant="title-large" color="#000000">{{ campaign.name }}</TypographyComponent>
      </BoxComponent>
      <FlexComponent :gap="8" wrap="wrap">
        <BadgeComponent
          :variant="
            campaign.status === CampaignStatusEnum.Active
              ? 'success'
              : campaign.status === CampaignStatusEnum.Scheduled
                ? 'info'
                : campaign.status === CampaignStatusEnum.Paused
                  ? 'warning'
                  : 'danger'
          "
        >
          {{ campaignStatusMap[campaign.status] }}
        </BadgeComponent>

        <BadgeComponent variant="outlined">{{ campaignTypeLabels[campaign.type] }}</BadgeComponent>

        <BadgeComponent v-if="Boolean(campaign.maxClicks && !campaign.perUserLimit)" variant="outlined">
          <FlexComponent class="clicks" :gap="4" align="center">
            Click limit: {{ clickValues.actual }}/{{ clickValues.max }}
            <div class="clicks__limit">
              <div class="clicks__limit__i" :style="{ width: `${clickValues.progress}%` }" />
            </div>
          </FlexComponent>
        </BadgeComponent>
      </FlexComponent>
      <BoxComponent v-if="endDate && endDateFull" :pt="8">
        <TooltipComponent>
          <TypographyComponent variant="label-medium" color="rgba(0, 0, 0, 0.48)">
            {{ campaign.status === CampaignStatusEnum.Ended ? 'Ended' : 'Ends' }} {{ endDate }}
          </TypographyComponent>
          <template #content>
            <TypographyComponent variant="body-small" white-space="nowrap">{{ endDateFull }}</TypographyComponent>
          </template>
        </TooltipComponent>
      </BoxComponent>
    </div>
    <div class="card__image">
      <ImageComponent v-if="campaign.image" :image="campaign.image" :alt="campaign.name" :width="192" :height="120" />
    </div>
  </FlexComponent>
</template>

<style scoped lang="scss">
.card {
  position: relative;
  padding: 8px 8px 8px 24px;
  border-radius: 16px;

  background: var(--white);
  border: 1px solid var(--secondary-8);

  &:hover {
    background: #fdfdfd;
    border-color: var(--secondary-8);
  }

  @include media-breakpoint-down(md) {
    flex-direction: column;
    align-items: initial;
    padding: 16px;
  }
}
.card--clickable {
  cursor: pointer;
}
.campaign-card__updates-indicator {
  border-radius: 16px;
  border: 1px solid rgba(234, 79, 133, 0.24);

  &:after {
    content: '';
    position: absolute;
    left: 16px;
    top: 16px;
    width: 8px;
    height: 8px;
    border-radius: 16px;
    background: #ea4f85;
  }
}
.card__image {
  height: 120px;
  width: 192px;
  overflow: hidden;
  border-radius: 8px;

  @include media-breakpoint-down(md) {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 114px;
    height: 64px;
  }

  img {
    height: 120px;
    width: 192px;
    object-fit: cover;
  }
}

.card__title {
  @include media-breakpoint-down(md) {
    min-height: 64px;
    padding-right: 130px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
  }
}

.card__title__i {
  @include media-breakpoint-down(md) {
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.clicks {
  white-space: nowrap;
}

.clicks__limit {
  width: 80px;
  height: 8px;
  border-radius: 8px;

  background: #eee;
}

.clicks__limit__i {
  height: 8px;
  border-radius: 8px;

  background: #269b58;
}
</style>
